import React from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import PCSpool from '../Spools/PCSpool';
import { isSpoolActive } from '../Spools/utils';
import * as PCConstants from '../PCConstants';
import { cadToThreeJsPositionWithOffset } from '../../Scenes/scene-utils';
import { IntersectionData } from '../intersectionTypes';
import { Model } from '../../hooks/useModelState';
import processIntersections from '../intersectionUtils';

interface MergedLineMeshProps {
  spools: PCSpool[];
  onFocus: (spool: PCSpool, worldPosition?: THREE.Vector3) => void;
  onSelect?: (spool: PCSpool, clickPoint?: { x: number; y: number; z: number }) => void;
  onIntersections?: (intersections: IntersectionData[]) => void;
  model: Model;
  offset: THREE.Vector3;
  scale: number;
  colourOverrides?: Record<string, string>;
  backgroundColour?: string;
}

interface ValidSpool {
  spool: PCSpool;
  start: THREE.Vector3;
  end: THREE.Vector3;
  midpoint: THREE.Vector3;
  index: number;
}

const hoverColor = new THREE.Color(PCConstants.HOVER_COLOUR);

// Dimmed at render time, so no repaint of spoolColour can overwrite it.
const baseColourOf = (spool: PCSpool, colourOverrides?: Record<string, string>, backgroundColour?: string): THREE.Color => {
  const colour = new THREE.Color(colourOverrides?.[spool.spoolId] ?? spool.spoolColour);
  return isSpoolActive(spool) ? colour : colour.lerp(new THREE.Color(backgroundColour ?? '#000000'), PCConstants.INACTIVE_SPOOL_FADE);
};

const MergedLineMesh = React.memo(({
  spools,
  onFocus,
  onSelect,
  onIntersections,
  model,
  offset,
  scale,
  colourOverrides,
  backgroundColour,
}: MergedLineMeshProps) => {
  const invalidate = useThree((state) => state.invalidate);
  const hoveredIndexRef = React.useRef<number>(-1);

  const validSpools = React.useMemo((): ValidSpool[] => {
    const result: ValidSpool[] = [];
    spools.forEach((spool, index) => {
      if (!spool.pointA || !spool.pointB) return;
      const [sx, sy, sz] = cadToThreeJsPositionWithOffset(spool.pointA.coords, offset, scale);
      const [ex, ey, ez] = cadToThreeJsPositionWithOffset(spool.pointB.coords, offset, scale);
      result.push({
        spool,
        start: new THREE.Vector3(sx, sy, sz),
        end: new THREE.Vector3(ex, ey, ez),
        midpoint: new THREE.Vector3((sx + ex) / 2, (sy + ey) / 2, (sz + ez) / 2),
        index,
      });
    });
    return result;
  }, [spools, offset, scale]);

  const lineSegmentsObj = React.useMemo(() => {
    if (validSpools.length === 0) return null;

    const positionBuffer = new Float32Array(validSpools.length * 6);
    const colorBuffer = new Float32Array(validSpools.length * 6);

    validSpools.forEach((vs, i) => {
      const base = i * 6;
      positionBuffer[base] = vs.start.x;
      positionBuffer[base + 1] = vs.start.y;
      positionBuffer[base + 2] = vs.start.z;
      positionBuffer[base + 3] = vs.end.x;
      positionBuffer[base + 4] = vs.end.y;
      positionBuffer[base + 5] = vs.end.z;

      const c = baseColourOf(vs.spool, colourOverrides, backgroundColour);
      colorBuffer[base] = c.r;
      colorBuffer[base + 1] = c.g;
      colorBuffer[base + 2] = c.b;
      colorBuffer[base + 3] = c.r;
      colorBuffer[base + 4] = c.g;
      colorBuffer[base + 5] = c.b;
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positionBuffer, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colorBuffer, 3));
    const material = new THREE.LineBasicMaterial({ vertexColors: true });

    return new THREE.LineSegments(geometry, material);
  }, [validSpools, colourOverrides, backgroundColour]);

  const setSpoolColor = React.useCallback((spoolIndex: number, color: THREE.Color) => {
    if (!lineSegmentsObj) return;
    const colorAttr = lineSegmentsObj.geometry.getAttribute('color') as THREE.BufferAttribute;
    const base = spoolIndex * 6;
    colorAttr.array[base] = color.r;
    colorAttr.array[base + 1] = color.g;
    colorAttr.array[base + 2] = color.b;
    colorAttr.array[base + 3] = color.r;
    colorAttr.array[base + 4] = color.g;
    colorAttr.array[base + 5] = color.b;
    colorAttr.needsUpdate = true;
  }, [lineSegmentsObj]);

  const getSpoolIndexFromIntersection = React.useCallback((e: any): number => {
    if (e.index != null) return Math.floor(e.index / 2);
    if (e.faceIndex != null) return Math.floor(e.faceIndex / 2);
    return -1;
  }, []);

  const handlePointerOver = React.useCallback((e: any) => {
    if (e.nativeEvent?.buttons) return;
    const spoolIndex = getSpoolIndexFromIntersection(e);
    if (spoolIndex < 0 || spoolIndex >= validSpools.length) return;
    if (hoveredIndexRef.current === spoolIndex) return;

    if (hoveredIndexRef.current >= 0 && hoveredIndexRef.current < validSpools.length) {
      const prev = validSpools[hoveredIndexRef.current];
      setSpoolColor(hoveredIndexRef.current, baseColourOf(prev.spool, colourOverrides, backgroundColour));
    }

    hoveredIndexRef.current = spoolIndex;
    setSpoolColor(spoolIndex, hoverColor);
    invalidate();
  }, [validSpools, setSpoolColor, invalidate, getSpoolIndexFromIntersection, colourOverrides, backgroundColour]);

  const handlePointerOut = React.useCallback((e: any) => {
    if (e.nativeEvent?.buttons) return;
    if (hoveredIndexRef.current < 0) return;

    const hasNodeIntersection = e.intersections?.some((i: any) => i.object.userData?.nodeId || i.object.userData?.instancedNodes);
    if (!hasNodeIntersection) {
      e.stopPropagation();
    }

    const vs = validSpools[hoveredIndexRef.current];
    if (vs) {
      setSpoolColor(hoveredIndexRef.current, baseColourOf(vs.spool, colourOverrides, backgroundColour));
    }
    hoveredIndexRef.current = -1;
    invalidate();
  }, [validSpools, setSpoolColor, invalidate, colourOverrides, backgroundColour]);

  const handleClick = React.useCallback((e: any) => {
    e.stopPropagation();
    const spoolIndex = getSpoolIndexFromIntersection(e);
    if (spoolIndex < 0 || spoolIndex >= validSpools.length) return;
    const { spool } = validSpools[spoolIndex];

    if (onIntersections && e.intersections) {
      const intersectionData = processIntersections({
        intersections: e.intersections,
        model,
        offset,
        scale,
        camera: e.camera,
        pointer: e.pointer,
      });
      onIntersections(intersectionData);
      return;
    }
    if (onSelect) {
      onSelect(spool);
    }
  }, [onIntersections, model, offset, scale, onSelect, validSpools, getSpoolIndexFromIntersection]);

  const handleDoubleClick = React.useCallback((e: any) => {
    e.stopPropagation();
    const spoolIndex = getSpoolIndexFromIntersection(e);
    if (spoolIndex < 0 || spoolIndex >= validSpools.length) return;
    const vs = validSpools[spoolIndex];
    onFocus(vs.spool, e.point);
  }, [onFocus, validSpools, getSpoolIndexFromIntersection]);

  // Reset hover state when spools change
  React.useEffect(() => {
    hoveredIndexRef.current = -1;
  }, [validSpools]);

  const constraintSpools = React.useMemo(
    () => validSpools.filter((vs) => vs.spool.showConstraintArrow),
    [validSpools],
  );

  React.useEffect(() => {
    if (!lineSegmentsObj) return;
    const spoolIds = validSpools.map((vs) => vs.spool.spoolId);
    lineSegmentsObj.userData = { mergedSpoolIds: spoolIds };
  }, [lineSegmentsObj]);

  if (validSpools.length === 0 || !lineSegmentsObj) return null;

  return (
    <>
      <primitive
        object={lineSegmentsObj}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onPointerMissed={() => {
          if (hoveredIndexRef.current >= 0 && hoveredIndexRef.current < validSpools.length) {
            const vs = validSpools[hoveredIndexRef.current];
            setSpoolColor(hoveredIndexRef.current, baseColourOf(vs.spool, colourOverrides, backgroundColour));
            hoveredIndexRef.current = -1;
            invalidate();
          }
        }}
      />
      {constraintSpools.map((vs) => (
        <arrowHelper
          key={`constraint_${vs.spool.spoolId}`}
          args={[
            new THREE.Vector3(0, -1, 0),
            new THREE.Vector3(vs.midpoint.x, vs.midpoint.y + 19 * scale, vs.midpoint.z),
            18 * scale,
            0xffcc00,
            9 * scale,
            9 * scale,
          ]}
        />
      ))}
    </>
  );
});

export default MergedLineMesh;
